import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import AIManagersService from 'src/services/AIManagersService';
import LeadStreamsService from 'src/services/LeadStreamsService';
import EventService from 'src/services/EventService';
import { shouldShowStreamInMenu } from 'src/utils/leadStreams';

import { deliverySeenPrefix } from './deliverySeen';
import { streamSummary } from './model';
import { acknowledgeEvent, buildCompanionEvents, eventStorageKey, readEventMemory, writeEventMemory } from './actions';

import type { CompanionEvent, ReviewQueue } from './actions';

import type { Stream } from 'src/services/LeadStreamsService/agent';

export default function useCompanionStatus(userId: number) {
    const client = useQueryClient();
    const key = ['companion-streams', userId];
    const [online, setOnline] = useState(navigator.onLine);
    const memory = useRef(readEventMemory(userId));
    const [revision, setRevision] = useState(0);
    const [eventState, setEventState] = useState<{ events: CompanionEvent[]; unread: CompanionEvent[] }>({
        events: [],
        unread: [],
    });
    const query = useQuery(key, async () => (await LeadStreamsService.getStreams()).filter(shouldShowStreamInMenu), {
        refetchInterval: 30000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: 10000,
        cacheTime: 0,
    });
    const reviewQuery = useQuery(
        ['companion-review', userId],
        async () => {
            const managers = await AIManagersService.getManagers();
            const results = await Promise.allSettled(
                managers.map(async (manager) => ({
                    managerId: manager.id,
                    name: manager.name,
                    conversations:
                        manager.stats.waitingReview > 0 ? await AIManagersService.getReviewQueue(manager.id) : [],
                }))
            );
            return {
                queues: results
                    .filter((r): r is PromiseFulfilledResult<ReviewQueue> => r.status === 'fulfilled')
                    .map((r) => r.value),
                failed: results.some((r) => r.status === 'rejected'),
            };
        },
        {
            refetchInterval: 30000,
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            staleTime: 10000,
            cacheTime: 0,
        }
    );
    useEffect(() => {
        const update = () => setOnline(navigator.onLine);
        window.addEventListener('online', update);
        window.addEventListener('offline', update);
        const event = EventService.subscribe<Stream | undefined>('LEAD_STREAMS_UPDATED', (_type, stream) => {
            // An older poll must not overwrite a just-confirmed pause/resume.
            void client.cancelQueries(['companion-streams', userId]).then(() => {
                const items = client.getQueryData<Stream[]>(['companion-streams', userId]);
                if (stream?.id && items)
                    client.setQueryData<Stream[]>(
                        ['companion-streams', userId],
                        items.map((item) => (item.id === stream.id ? stream : item))
                    );
                void client.invalidateQueries(['companion-streams', userId]);
            });
        });
        const managerEvent = EventService.subscribe('AI_MANAGERS_UPDATED', () => {
            void client.invalidateQueries(['companion-review', userId]);
        });
        const storage = (e: StorageEvent) => {
            if (e.key === eventStorageKey(userId) || e.key?.startsWith(deliverySeenPrefix(userId))) {
                memory.current = readEventMemory(userId);
                setRevision((v) => v + 1);
            }
        };
        window.addEventListener('storage', storage);
        return () => {
            managerEvent.unsubscribe();
            window.removeEventListener('storage', storage);
            event.unsubscribe();
            window.removeEventListener('online', update);
            window.removeEventListener('offline', update);
        };
    }, [client, userId]);
    useEffect(() => {
        const result = buildCompanionEvents(
            memory.current,
            online && !query.isError ? query.data : undefined,
            online && !reviewQuery.isError ? reviewQuery.data?.queues : undefined
        );
        const saved =
            JSON.stringify(result.memory) !== JSON.stringify(memory.current)
                ? writeEventMemory(userId, result.memory)
                : result.memory;
        const final =
            saved === result.memory
                ? result
                : buildCompanionEvents(
                      saved,
                      online && !query.isError ? query.data : undefined,
                      online && !reviewQuery.isError ? reviewQuery.data?.queues : undefined
                  );
        memory.current = final.memory;
        setEventState({ events: final.events, unread: final.unread });
    }, [query.data, query.isError, reviewQuery.data, reviewQuery.isError, online, userId, revision]);
    const acknowledge = (event: CompanionEvent) => {
        memory.current = writeEventMemory(userId, acknowledgeEvent(memory.current, event));
        setRevision((v) => v + 1);
    };
    const streams = query.data || [];
    const available = online && !query.isError && !!query.data;
    const summary =
        query.isLoading && online
            ? { state: 'loading' as const, label: 'Проверяю', text: 'Обновляю состояние потоков…' }
            : streamSummary(streams, available);
    return {
        streams,
        available,
        summary,
        events: eventState.events,
        unreadEvents: eventState.unread,
        acknowledge,
        reviewUnavailable: reviewQuery.isError || !!reviewQuery.data?.failed,
        refresh: () => Promise.allSettled([query.refetch(), reviewQuery.refetch()]),
        refreshing: query.isFetching || reviewQuery.isFetching,
    };
}
